import {
    BaseLayer,
    BaseLayerFactory_3857,
    BaseLayerType
} from "esdm-map";

import { IBaseLayer } from "esdm-map/dist/baselayers/interfaces/IBaseLayer";
import TileLayer from "ol/layer/Tile";
import TileWMS from 'ol/source/TileWMS';

import * as olProj from 'ol/proj';
import proj4 from "proj4";
import { register } from 'ol/proj/proj4';
import Projection from "ol/proj/Projection";
import XYZ from 'ol/source/XYZ';
import OSM from 'ol/source/OSM';
import ImageLayer from "ol/layer/Image";
import ImageWMS from 'ol/source/ImageWMS';
import WMTS from 'ol/source/WMTS'; 
import WMTSTileGrid from 'ol/tilegrid/WMTS';

import { get as getProjection } from "ol/proj";
import { getWidth } from "ol/extent";
import TileGrid from "ol/tilegrid/TileGrid"

export const NorthantsBaseLayerTypes = {
    ...BaseLayerType,
    NorthamptonshireAerial: 996,
    NorthamptonshireOS6inch: 997,
    NorthamptonshireEALidar: 998,
    NorthamptonshireOS: 999
};

export class NorthantsBaseMapping_3857 extends BaseLayerFactory_3857 {

    public GetBaseLayerByType(baseLayerType: BaseLayerType, bingApiKey: string, esdmOsmApiKey:string): IBaseLayer {
        if (baseLayerType === NorthantsBaseLayerTypes.NorthamptonshireOS) {
            return this.GetNorthantsOS();
        }

        if (baseLayerType === NorthantsBaseLayerTypes.NorthamptonshireAerial) {
            return this.GetNorthantsAerial();
        }

        if (baseLayerType === NorthantsBaseLayerTypes.NorthamptonshireEALidar) {
            return this.GetNorthantsEALidar();
        }

        if (baseLayerType === NorthantsBaseLayerTypes.NorthamptonshireOS6inch) {
            return this.GetNorthantsOS6inch_1888_1913();
        }
        
        return super.GetBaseLayerByType(baseLayerType, bingApiKey, esdmOsmApiKey);
    }   

    private GetNorthantsOS(): IBaseLayer {
        
        let lyr = new TileLayer({
            visible: true,
            source: new WMTS({
                //Configured via OGC Proxy
                url: "/api/OGCService/NorthantsOS",
                layer: "Road_3857",
                matrixSet: "EPSG:3857",
                format: "image/png",
                projection: "EPSG:3857",
                tileGrid: new WMTSTileGrid({
                    origin: [-20037508.342787, 20037508.342787],
                    resolutions: [1222.9924523698928, 611.4962261850814, 305.7481130924087, 152.87405654633633, 76.43702827303586, 38.21851413651793, 19.109257068258966, 9.554628534129483, 4.777314267197037, 2.3886571335985134, 1.1943285666669667, 0.5971642834657753, 0.2985821416005962, 0.1492910708002979],
                    matrixIds: ["EPSG:3857:7", "EPSG:3857:8", "EPSG:3857:9", "EPSG:3857:10", "EPSG:3857:11", "EPSG:3857:12", "EPSG:3857:13", "EPSG:3857:14", "EPSG:3857:15", "EPSG:3857:16", "EPSG:3857:17", "EPSG:3857:18", "EPSG:3857:19", "EPSG:3857:20"]
                }),
                style: "default",
                crossOrigin: "Anonymous",
                attributions: [`© Crown copyright and database rights ${new Date().getFullYear()} OS 100063706.`,
                    'Use of this data is subject to <a href="/map-terms-and-conditions#os">terms and conditions</a>.']
            })
        });
        return new BaseLayer("NorthamptonshireOS", "Ordnance Survey", lyr);
    } 

    private GetNorthantsAerial(): IBaseLayer {
        let lyr = new TileLayer({
            visible: false,
            source: new TileWMS({
                //Configured via OGC Proxy
                url: "/api/OGCService/NorthantsAerial",
                params: {
                    LAYERS: 'APGB_Latest_UK_125mm',
                    FORMAT: 'image/png',
                    SRS: 'EPSG:3857'
                },
                projection: "EPSG:3857",
                crossOrigin: "Anonymous",
                attributions: ['© Bluesky International Limited and Getmapping Limited 1999 - 2023',
                    '© Bluesky International Limited 2024 and onwards.',
                    'Use of this data is subject to <a href="/map-terms-and-conditions#apgb">terms and conditions</a>']
            })
        });
        return new BaseLayer("NorthamptonshireAerial", "Aerial Photography", lyr);
    }

    private GetNorthantsEALidar(): IBaseLayer {
        const extent3857 = getProjection('EPSG:3857').getExtent();
        const startResolution = getWidth(extent3857) / 1024;
        const resolutions = new Array(20);
        for (let i = 0, ii = resolutions.length; i < ii; ++i) {
            resolutions[i] = startResolution / Math.pow(2, i);
        }

        const grid1024 = new TileGrid({
            extent: olProj.transformExtent([-7.68, 49.8, 1.77, 60.4], 'EPSG:4326', 'EPSG:3857'),
            resolutions: resolutions,
            tileSize: [1024, 1024],
        });

        let lyr = new TileLayer({
            visible: false,
            source: new TileWMS({
                url: "https://environment.data.gov.uk/spatialdata/lidar-composite-digital-terrain-model-dtm-1m-2022/wms",
                params: {
                    LAYERS: 'Lidar_Composite_Hillshade_DTM_1m',
                    FORMAT: 'image/png',
                    SRS: 'EPSG:3857'
                },
                projection: "EPSG:3857",
                tileGrid: grid1024,
                crossOrigin: "Anonymous",
                attributions: [`© Environment Agency copyright and/or database right ${new Date().getFullYear()}. All rights reserved.`]
            })
        });
        return new BaseLayer("NorthamptonshireEALidar", "Lidar DTM 1m", lyr);
    }

    protected GetNorthantsOS6inch_1888_1913(): IBaseLayer {
        const lyr = new TileLayer({
            extent: olProj.transformExtent([-7.68, 49.8, 1.77, 60.4], 'EPSG:4326', 'EPSG:3857'),
            preload: Infinity,
            source: new XYZ({
                urls: [
                    'https://api.maptiler.com/tiles/uk-osgb10k1888/{z}/{x}/{y}.jpg?key=5aCZ3KX2j1ZtTrkvo8Wg'
                ],
                minZoom: 1,
                maxZoom: 18,
                attributions: 'Mapping from <a href="https://www.nls.uk/">National Library of Scotland</a> <img class="maptiler-logo" src="/images/maptiler-logo-adaptive.svg" alt="MapTiler logo" />',
            }),
            visible: false,
        });
        lyr.set("name", "OS6inch_1888_1913", true);
        return new BaseLayer("NorthamptonshireOS6inch", "OS 6 inch to the mile (1888-1913)", lyr);
    }
}